import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  IUserCredentials,
  IStandardGUJRequestParams,
  IReduxRequestNode,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";

interface IUserCredentialsState extends IUserCredentials {
  actions: {
    changeCredentialsCSRF: IUserCredentialsGUJNode;
  };
}

const initialState: IUserCredentialsState = {
  isAnon: true,
  userID: "0",
  userSID: "anonimous-empty-sid",
  name: "Anonymous",
  groups: [8, 16],
  lfrom: "",
  actions: {
    changeCredentialsCSRF: {},
  },
};

interface IUserCredentialsParamsGUJ {
  first_name?: string;
  last_name?: string;
  nickname?: string;
  descr?: string;
  birth_date?: string;
}

type IUserCredentialsRequestGUJ = IStandardGUJRequestParams &
  IUserCredentialsParamsGUJ;
interface IUserCredentialsResponseGUJ {
  status: "ok" | "error";
}

type IUserCredentialsGUJNode = IReduxRequestNode<
  IUserCredentialsRequestGUJ,
  IUserCredentialsResponseGUJ
>;

export const changeCredentialsCSRF = createAsyncThunk(
  "user/credentialsSlice/changeCredentialsCSRF",
  async (params: {
    data: IUserCredentials;
    onSuccess?: (success: IUserCredentialsGUJNode) => void;
    onError?: (error: IUserCredentialsGUJNode) => void;
  }) => {
    const response = new Request.CSRF<
      IUserCredentialsRequestGUJ,
      IUserCredentialsResponseGUJ
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "update_my_data_ajax",
      },
      request: {
        method: "POST",
        data: {
          action: "update_my_data_ajax",
          gu_ajax: true,
          ...((params.data.firstName || params.data.firstName === "") && {
            first_name: params.data.firstName,
          }),
          ...((params.data.lastName || params.data.lastName === "") && {
            last_name: params.data.lastName,
          }),
          ...((params.data.nickname || params.data.nickname === "") && {
            nickname: params.data.nickname,
          }),
          ...((params.data.description || params.data.description === "") && {
            descr: params.data.description,
          }),
          ...((params.data.birthDate || params.data.birthDate === "") && {
            birth_date: params.data.birthDate,
          }),
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const credentialsSlice = createSlice({
  name: "user/credentialsSlice",
  initialState,
  reducers: {
    setUserCredentials: (
      state: IUserCredentialsState,
      action: PayloadAction<IUserCredentials>
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "user.credentials");
    // Загрузка
    builder.addCase(
      changeCredentialsCSRF.pending,
      (state: IUserCredentialsState) => {
        state.actions.changeCredentialsCSRF.isLoading = true;
      }
    );
    // Ответ
    builder.addCase(
      changeCredentialsCSRF.fulfilled,
      (
        state: IUserCredentialsState,
        action: PayloadAction<IUserCredentialsGUJNode>
      ) => {
        state.actions.changeCredentialsCSRF = action.payload;
        state.actions.changeCredentialsCSRF.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error("Ошибка запроса credentialsCSRF", action.payload.error);
          state.actions.changeCredentialsCSRF.error = action.payload.error;
        }
      }
    );
  },
});

export const { setUserCredentials } = credentialsSlice.actions;

export default credentialsSlice.reducer;
